
import { supabase } from "@/integrations/supabase/client";
import type { Activity } from "../ActivityTypes";
import { mockActivities } from "../mockActivities"; // Fallback mock data
import { publishA2AMessage } from "../utils/a2aActivityMessaging"; // We'll create this util

/** Fetches suggested activities for a target date */
export const fetchSuggestedActivities = async (targetDate?: Date): Promise<Activity[]> => {
  try {
    const body: { limit: number; date?: string } = { limit: 5 };
    if (targetDate) {
      body['date'] = targetDate.toISOString();
    }
    
    publishA2AMessage('data-fetch', `Fetching suggested activities${targetDate ? ' for ' + targetDate.toLocaleDateString() : ''}`, { targetDate: targetDate?.toISOString() });
    
    const { data, error } = await supabase.functions.invoke('generate-activities', { body });
    if (error) throw error;

    const sourceActivities = (data.activities || data.summaries || []).map((summary: any) => {
      if ('priority' in summary && 'type' in summary) {
        return {
          ...summary,
          status: summary.status || "pending",
        } as Activity;
      }
      let activityType: Activity["type"] = "email";
      let priority: Activity["priority"] = "medium";
      if ((summary.subject || "").toLowerCase().includes("interview")) {
        activityType = "meeting";
        priority = "high";
      } else if ((summary.subject || "").toLowerCase().includes("follow")) {
        activityType = "followup";
      }
      return {
        id: summary.id || Math.random().toString(36).substring(2, 11),
        title: summary.subject,
        description: summary.summary,
        priority,
        type: activityType,
        time: summary.receivedAt
          ? new Date(summary.receivedAt).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })
          : undefined,
        status: "pending",
        source: "email", // Assuming source is email if not specified otherwise from summary
        related_entity: summary.assignedTo
          ? {
              type: summary.assignedTo.type,
              id: summary.assignedTo.id,
              name: summary.assignedTo.name,
            }
          : undefined,
      } as Activity;
    });

    publishA2AMessage('data-fetch-success', `Successfully fetched ${sourceActivities.length} suggested activities`, { activityCount: sourceActivities.length });

    return sourceActivities.sort((a, b) => {
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      const priorityDiff = priorityOrder[b.priority] - priorityOrder[a.priority];
      if (priorityDiff !== 0) return priorityDiff;
      if (a.time && b.time) return a.time.localeCompare(b.time);
      if (a.time) return -1;
      if (b.time) return 1;
      return 0;
    });
  } catch (error) {
    console.error("Error fetching suggested activities:", error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    publishA2AMessage('data-fetch-error', `Error fetching activities: ${errorMessage}`, { error: errorMessage }, 'high');
    
    return mockActivities; // Fallback to mock activities on error
  }
};
